// src/utils/navigate.js
//
// In-app navigation for a single-page application.
//
// WHY THIS EXISTS
// ---------------
// `src/main.jsx` routes by listening for `popstate` and re-reading
// `window.location.pathname` (see the onPop handler). So an in-app navigation is
// `history.pushState` followed by a synthetic PopStateEvent. That idiom already
// existed inline (main.jsx:2049, AuraWorkspace.jsx:627); this centralises it.
//
// The nav was instead doing `window.location.href = "/the-edit"`, which is a FULL
// DOCUMENT NAVIGATION. It tears the SPA down and reboots it: the browser paints
// whatever HTML the server returns for that URL, then React's createRoot()
// discards that DOM (~1.7s) and rebuilds the page (~5s of main-thread work).
// That is the "I click a link and the site reloads" behaviour.
//
// Reserve a real document navigation for the cases that genuinely need one:
//   • cross-origin targets (cannot be routed client-side)
//   • auth boundaries where a fresh JWT / clean provider tree is the point
//     (admin login, logout, exiting admin preview), those call
//     window.location.* directly and are deliberately NOT routed through here.

/**
 * True only for same-origin, root-relative paths ("/the-edit", "/italy?x=1").
 *
 * Everything else is treated as external and gets a real navigation. In
 * particular this must reject:
 *   "//evil.com/x"      protocol-relative, the browser reads this as cross-origin
 *   "https://x.com"     absolute
 *   "mailto:", "tel:", "javascript:"   any scheme
 * Routing those client-side would silently swallow the navigation, and in the
 * "//" case would push a path the router cannot resolve.
 */
export function isInternalPath(target) {
  if (typeof target !== "string" || target === "") return false;
  if (target.startsWith("//")) return false;              // protocol-relative
  if (/^[a-zA-Z][a-zA-Z0-9+.\-]*:/.test(target)) return false; // any scheme
  return target.startsWith("/");
}

/**
 * Navigate without reloading the document when we can; fall back to a real
 * navigation when we cannot.
 *
 * @param {string} target  A root-relative path, or an absolute/external URL.
 * @param {{ newTab?: boolean }} [opts]
 */
export function navigateTo(target, opts = {}) {
  if (typeof window === "undefined") return;

  if (opts.newTab) {
    window.open(target, "_blank", "noreferrer");
    return;
  }

  if (!isInternalPath(target)) {
    window.location.href = target;
    return;
  }

  const here = window.location.pathname + window.location.search + window.location.hash;
  if (target === here) return; // already there, don't push a duplicate entry

  window.history.pushState(null, "", target);
  // main.jsx's popstate listener re-derives page state from location.pathname.
  window.dispatchEvent(new PopStateEvent("popstate"));
}

// ── Anchor interception ──────────────────────────────────────────────────────
//
// The prerendered pages carry real <a href> links (49 on the homepage), which is
// exactly what we want for crawlers, keyboard users and AI agents reading the
// HTML. But nothing in this app intercepted their clicks, so every one was a full
// document navigation. Before React mounts (~1.7s) they still are, the DOM is
// server HTML with no listeners. After mount, this handler routes them in-app.
//
// We keep the href. We only stop the browser's default navigation.

// Paths Cloudflare answers itself. Routing these client-side would swallow a real
// request. Kept in sync with functions/ and the 301s in public/_redirects.
const SERVER_HANDLED = [
  "/assets/",   // hashed bundles, functions/assets/[[path]].js
  "/img/",      // functions/img
  "/cdn-cgi/",  // Cloudflare
];

// Server-side 301s in public/_redirects. Intercepting these would leave the user
// on a non-canonical URL that the server would otherwise have corrected.
const SERVER_REDIRECTS = [
  "/venues",        // → /wedding-venues
  "/destinations",  // → canonical geo URLs
  "/couple/login",  // → /getting-married/login
  "/couple/signup", // → /getting-married/signup
  "/login",         // → /getting-married/login
];

function isServerRoute(pathname) {
  if (/\.[a-zA-Z0-9]{2,5}$/.test(pathname)) return true; // /feed.xml, /robots.txt, /sitemap.xml, any file
  if (SERVER_HANDLED.some((p) => pathname.startsWith(p))) return true;
  if (SERVER_REDIRECTS.some((p) => pathname === p || pathname.startsWith(p + "/"))) return true;
  return false;
}

function onDocumentClick(e) {
  if (e.defaultPrevented) return;       // a component already handled it
  if (e.button !== 0) return;           // left click only, middle click opens a tab
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // open-in-tab / download / new window

  const anchor = e.target?.closest?.("a");
  if (!anchor) return;
  if (anchor.target && anchor.target !== "_self") return;       // target="_blank"
  if (anchor.hasAttribute("download")) return;
  if (/\bexternal\b/.test(anchor.getAttribute("rel") || "")) return;

  const href = anchor.getAttribute("href");
  if (!href || href.startsWith("#")) return;                    // in-page anchor
  if (!isInternalPath(href)) return;                            // external, mailto:, tel:, //host

  const url = new URL(href, window.location.origin);
  if (isServerRoute(url.pathname)) return;
  // Same page, different hash, let the browser scroll natively.
  if (url.pathname === window.location.pathname && url.hash) return;

  e.preventDefault();
  navigateTo(href);
}

/**
 * Install the global anchor handler. Returns a cleanup function.
 * Bubble phase, so any component calling preventDefault() still wins.
 */
export function installLinkInterceptor() {
  if (typeof document === "undefined") return () => {};
  document.addEventListener("click", onDocumentClick);
  return () => document.removeEventListener("click", onDocumentClick);
}

export default navigateTo;
